/**
 * @file
 * Real Seal floating button and modal behavior.
 */
(function (Drupal, once) {
  'use strict';

  var MODAL_NAME = 'Tennessee Real Seal';

  var locked = false;
  var savedOverflow = '';
  var savedPaddingRight = '';
  var fixups = [];

  /**
   * Collects elements laid out against the viewport, with their geometry.
   *
   * Absolutely positioned elements are included because one with no
   * positioned ancestor resolves against the initial containing block and so
   * moves for the same reason a fixed one does. Anything that turns out not
   * to have moved is simply left alone later.
   *
   * @return {Array}
   *   One record per candidate in document order — `holdFixed()` relies on
   *   ancestors coming before their descendants. Each record holds the
   *   element, its width and its right edge.
   */
  function measureViewportAnchored() {
    var all = document.body.getElementsByTagName('*');
    var found = [];
    var position;
    var rect;
    var i;

    for (i = 0; i < all.length; i++) {
      // The seal's own trigger and dialog are meant to follow the viewport:
      // the dialog re-centres itself once the scrollbar is gone, which is
      // correct, so pinning them would be what knocked them out of place.
      if (all[i].closest && all[i].closest('.floating-real-seal')) {
        continue;
      }

      position = window.getComputedStyle(all[i]).position;

      if (position === 'fixed' || position === 'absolute') {
        rect = all[i].getBoundingClientRect();
        found.push({el: all[i], width: rect.width, right: rect.right});
      }
    }

    return found;
  }

  /**
   * Holds those elements where they were before the scrollbar was removed.
   *
   * They are laid out against the viewport, not against the root element, so
   * the padding that keeps the rest of the page still cannot reach them: a
   * full-width one grows into the reclaimed space and anything anchored right
   * slides into it. Both are measured and pinned back.
   *
   * Do NOT split this into a read pass and a write pass. `before` is in
   * document order, so an ancestor is always pinned before its descendants
   * are measured, and a descendant of something already pinned reads as
   * unmoved and is correctly left alone. Measuring everything up front
   * instead makes a descendant look moved as well and it gets compensated a
   * second time — on Bike TN that is `#site-header` (fixed, `width: 100%`)
   * and the `.block-menu-utility` anchored to its right edge.
   *
   * The interleaving costs a reflow per element actually pinned, which is a
   * handful; batching would trade that for a wrong result.
   *
   * @param {Array} before
   *   Records from `measureViewportAnchored()`, in document order, taken
   *   before the lock.
   * @param {number} gutter
   *   The width the scrollbar had given up.
   */
  function holdFixed(before, gutter) {
    before.forEach(function (record) {
      var rect = record.el.getBoundingClientRect();
      var style = record.el.style;
      var marginRight;

      if (Math.abs(rect.width - record.width) >= 1) {
        fixups.push({el: record.el, prop: 'maxWidth', was: style.maxWidth});
        style.maxWidth = record.width + 'px';
      }
      else if (Math.abs(rect.right - record.right) >= 1) {
        marginRight = parseFloat(
          window.getComputedStyle(record.el).marginRight) || 0;
        fixups.push({el: record.el, prop: 'marginRight', was: style.marginRight});
        style.marginRight = (marginRight + gutter) + 'px';
      }
    });
  }

  /**
   * Locks page scrolling, taking the scrollbar away without moving anything.
   *
   * `overflow: hidden` removes the scrollbar outright, and the width it gives
   * up is handed straight back as padding so the page does not reflow. Note
   * that `scrollbar-gutter: stable` must NOT be used for this: it shrinks the
   * basis that `vw` resolves against, which moves anything sized that way —
   * the Bike TN nav is spaced in `vw`.
   */
  function lockScroll() {
    var html = document.documentElement;
    var gutter = window.innerWidth - html.clientWidth;
    var before = [];
    var current;

    if (locked) {
      return;
    }

    savedOverflow = html.style.overflow;
    savedPaddingRight = html.style.paddingRight;
    fixups = [];

    if (gutter > 0) {
      before = measureViewportAnchored();
      current = parseFloat(window.getComputedStyle(html).paddingRight) || 0;
      html.style.paddingRight = (current + gutter) + 'px';
    }

    html.style.overflow = 'hidden';

    if (gutter > 0) {
      holdFixed(before, gutter);
    }

    locked = true;
  }

  /**
   * Releases the lock, restoring exactly what was there before.
   */
  function unlockScroll() {
    var html = document.documentElement;

    if (!locked) {
      return;
    }

    fixups.forEach(function (fixup) {
      fixup.el.style[fixup.prop] = fixup.was;
    });
    fixups = [];

    html.style.overflow = savedOverflow;
    html.style.paddingRight = savedPaddingRight;
    locked = false;
  }

  /**
   * Pushes a modal interaction onto the data layer.
   *
   * @param {string} name
   *   The name reported for the modal.
   * @param {string} details
   *   Either 'open' or 'close'.
   */
  function pushEvent(name, details) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      modal_name: name,
      event_details: details,
      event: 'popup_modal'
    });
  }

  Drupal.behaviors.realSeal = {
    attach: function (context) {
      once('real-seal', '.floating-real-seal', context).forEach(function (wrapper) {
        var trigger = wrapper.querySelector('.real-seal__trigger');
        var dialog = wrapper.querySelector('.real-seal__dialog');
        var closeBtn = wrapper.querySelector('.real-seal__close');

        if (!trigger || !dialog) {
          return;
        }

        // Without <dialog> support there is no modal to open, so leave the
        // trigger out of the tab order rather than offering a dead control.
        if (typeof dialog.showModal !== 'function') {
          wrapper.hidden = true;
          return;
        }

        // The trigger floats over the page at the bottom left, so it reads as
        // the last thing on the page and should be the last tab stop. Where
        // the block is placed decides that, and the obvious region — Pre Page
        // — renders first in `page.html.twig`, which would make the trigger
        // the FIRST tab stop instead. Moving the wrapper to the end of <body>
        // settles the focus order no matter which region the author picks,
        // and has the side benefit that no ancestor `transform`/`filter` can
        // become the containing block for `position: fixed`.
        //
        // Nothing here is styled through its ancestors — every selector in
        // the component and in the brand skins starts at `.floating-real-seal`
        // — so the move is safe. The block's cache-metadata flush stays behind
        // in the region, which is what it is for.
        if (wrapper.parentNode !== document.body) {
          document.body.appendChild(wrapper);
        }

        trigger.addEventListener('click', function () {
          dialog.showModal();
          lockScroll();
          pushEvent(MODAL_NAME, 'open');
        });

        if (closeBtn) {
          closeBtn.addEventListener('click', function () {
            dialog.close();
          });
        }

        // Only a click on the backdrop resolves to the dialog element itself;
        // the visible content sits in a child element.
        dialog.addEventListener('click', function (event) {
          if (event.target === dialog) {
            dialog.close();
          }
        });

        // Fires for the close button, the backdrop click and Esc alike.
        dialog.addEventListener('close', function () {
          unlockScroll();
          pushEvent(MODAL_NAME, 'close');
        });
      });
    }
  };

})(Drupal, once); // eslint-disable-line no-undef
